import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Calendar,
  DollarSign,
  Clock,
  Car,
  Building2,
  PieChart as PieChartIcon
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { getAgencyColorByAgency, AgencyDot } from "@/lib/agencyColors";
import { LoanReturnAlerts } from "@/components/LoanReturnAlerts";

// Couleurs pour le camembert
const PIE_COLORS = [
  '#FEED00', '#3B82F6', '#A855F7', '#22C55E', '#F97316', 
  '#EC4899', '#14B8A6', '#EF4444', '#8B5CF6', '#F59E0B',
  '#06B6D4', '#84CC16', '#6366F1', '#D946EF', '#0EA5E9'
];

export default function DashboardPage() {
  const { user, agencies } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedAgency]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      
      let statsUrl = `${API}/statistics`;
      let appointmentsUrl = `${API}/appointments?date=${today}`;
      
      if (selectedAgency !== "all") {
        statsUrl += `?agency_id=${selectedAgency}`;
        appointmentsUrl += `&agency_id=${selectedAgency}`;
      }
      
      const [statsRes, appointmentsRes] = await Promise.all([
        axios.get(statsUrl),
        axios.get(appointmentsUrl)
      ]);
      
      setStats(statsRes.data);
      setTodayAppointments(appointmentsRes.data.filter(apt => apt.status !== "cancelled"));
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency?.city || "—";
  };

  const getStatusBadge = (status) => {
    const variants = {
      scheduled: { class: "badge-info", label: "Planifié" },
      completed: { class: "badge-success", label: "Terminé" },
      cancelled: { class: "badge-danger", label: "Annulé" }
    };
    const variant = variants[status] || variants.scheduled;
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${variant.class}`}>{variant.label}</span>;
  };

  // Préparer les données pour le camembert
  const getPieChartData = () => {
    if (!stats?.month_prestation_percentages) return [];
    
    return Object.entries(stats.month_prestation_percentages)
      .map(([name, data]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        fullName: name,
        value: data.count,
        percentage: data.percentage
      }))
      .sort((a, b) => b.value - a.value);
  };

  const pieChartData = getPieChartData();

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-black/90 border border-gray-700 rounded-lg px-3 py-2 shadow-lg">
          <p className="text-white font-medium text-sm">{data.fullName}</p>
          <p className="text-[#FEED00] text-sm">{data.value} RDV ({data.percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 rounded skeleton" style={{ background: '#1A1A1A' }} />
          <div className="h-10 w-40 rounded skeleton" style={{ background: '#1A1A1A' }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 rounded-lg skeleton" style={{ background: '#1A1A1A' }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl sm:text-3xl font-bold"
            style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}
          >
            TABLEAU DE BORD
          </h1>
          <p className="text-gray-500 mt-1">
            {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
          </p>
        </div>
        
        {(user?.role === "admin" || user?.role === "associe") && (
          <Select value={selectedAgency} onValueChange={setSelectedAgency}>
            <SelectTrigger 
              className="w-[180px] border text-gray-200" 
              style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}
              data-testid="agency-filter"
            >
              <Building2 className="w-4 h-4 mr-2 text-gray-600" />
              <SelectValue placeholder="Toutes les agences" />
            </SelectTrigger>
            <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
              <SelectItem value="all" className="text-gray-200 focus:bg-white/5 focus:text-white">
                Toutes les agences
              </SelectItem>
              {agencies.map((agency) => (
                <SelectItem 
                  key={agency.id} 
                  value={agency.id}
                  className="text-gray-200 focus:bg-white/5 focus:text-white"
                >
                  <span className="flex items-center gap-2">
                    <AgencyDot color={getAgencyColorByAgency(agency)} />
                    {agency.city}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Alertes retour véhicules de prêt */}
      <LoanReturnAlerts agencies={agencies} selectedAgency={selectedAgency} />

      {/* Stats Grid - 4 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Clients */}
        <Card className="stat-card stat-card-hp" data-testid="stat-clients">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Clients</p>
                <p 
                  className="text-3xl font-bold text-white" 
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  {stats?.total_clients || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1">Fiches clients créées</p>
              </div>
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(254, 237, 0, 0.15)' }}
              >
                <Users className="w-5 h-5" style={{ color: '#FEED00' }} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* RDV Aujourd'hui */}
        <Card className="stat-card stat-card-hp" data-testid="stat-today">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">RDV Aujourd'hui</p>
                <p 
                  className="text-3xl font-bold text-white" 
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  {stats?.today_appointments || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1">Planifiés pour aujourd'hui</p>
              </div>
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(16, 185, 129, 0.15)' }}
              >
                <Calendar className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CA Prévisionnel du Jour - Visible uniquement pour admin/manager */}
        {user?.role !== "mecano" && (
          <Card className="stat-card stat-card-hp" data-testid="stat-revenue">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">CA Prévisionnel</p>
                  <p 
                    className="text-3xl font-bold" 
                    style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00' }}
                  >
                    {(stats?.today_potential_revenue || 0).toLocaleString('fr-FR')} €
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Potentiel du jour</p>
                </div>
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(254, 237, 0, 0.15)' }}
                >
                  <DollarSign className="w-5 h-5" style={{ color: '#FEED00' }} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* RDV Semaine - Pour tous */}
        <Card className="stat-card stat-card-hp" data-testid="stat-week">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">RDV Semaine</p>
                <p 
                  className="text-3xl font-bold text-white" 
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  {stats?.week_appointments || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1">Cette semaine</p>
              </div>
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: 'rgba(168, 85, 247, 0.15)' }}
              >
                <Clock className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stat supplémentaire pour mecano */}
        {user?.role === "mecano" && (
          <Card className="stat-card stat-card-hp" data-testid="stat-completed">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">RDV Terminés</p>
                  <p 
                    className="text-3xl font-bold text-white" 
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  >
                    {stats?.completed_appointments || 0}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Total terminés</p>
                </div>
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: 'rgba(16, 185, 129, 0.15)' }}
                >
                  <Calendar className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Second Row: Pie Chart + Today's Appointments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart - Prestations du Mois */}
        {user?.role !== "mecano" && pieChartData.length > 0 && (
          <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
            <CardHeader className="border-b pb-4" style={{ borderColor: '#1A1A1A' }}>
              <CardTitle 
                className="text-lg flex items-center gap-2" 
                style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}
              >
                <PieChartIcon className="w-5 h-5" />
                RÉPARTITION PRESTATIONS DU MOIS
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percentage }) => `${percentage}%`}
                      labelLine={false}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                          stroke="#0A0A0A"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      layout="vertical" 
                      align="right" 
                      verticalAlign="middle"
                      formatter={(value) => <span className="text-gray-300 text-xs">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Stats summary under pie chart */}
              <div className="mt-4 pt-4 border-t" style={{ borderColor: '#1A1A1A' }}>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {pieChartData.reduce((acc, item) => acc + item.value, 0)}
                    </p>
                    <p className="text-xs text-gray-500">RDV ce mois</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00' }}>
                      {pieChartData.length}
                    </p>
                    <p className="text-xs text-gray-500">Types de prestations</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-400" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {(stats?.month_revenue || 0).toLocaleString('fr-FR')} €
                    </p>
                    <p className="text-xs text-gray-500">CA réalisé (mois)</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Appointments */}
        <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
          <CardHeader className="border-b pb-4" style={{ borderColor: '#1A1A1A' }}>
            <div className="flex items-center justify-between">
              <CardTitle 
                className="text-lg flex items-center gap-2" 
                style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}
              >
                <Clock className="w-5 h-5" />
                RENDEZ-VOUS DU JOUR
              </CardTitle>
              <Badge variant="outline" style={{ borderColor: '#FEED00', color: '#FEED00' }}>
                {todayAppointments.length} RDV
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {todayAppointments.length === 0 ? (
              <div className="p-8 text-center text-gray-600">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun rendez-vous prévu aujourd'hui</p>
              </div>
            ) : (
              <div className="divide-y max-h-[400px] overflow-y-auto" style={{ borderColor: '#1A1A1A' }}>
                {todayAppointments
                  .sort((a, b) => a.time.localeCompare(b.time))
                  .map((appointment) => (
                  <div 
                    key={appointment.id} 
                    className="p-4 flex items-center gap-4 table-row-hover"
                    data-testid={`appointment-${appointment.id}`}
                  >
                    <div className="w-16 text-center">
                      <span 
                        className="text-lg font-bold" 
                        style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00' }}
                      >
                        {appointment.time}
                      </span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white">{appointment.client_name}</span>
                        {getStatusBadge(appointment.status)}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Car className="w-3.5 h-3.5" />
                          {appointment.vehicle}
                        </span>
                        <span className="font-mono text-xs">{appointment.immatriculation}</span>
                      </div>
                    </div>
                    
                    <div className="text-right hidden sm:block">
                      <Badge 
                        className="text-gray-300 hover:bg-white/10"
                        style={{ background: '#1A1A1A' }}
                      >
                        {appointment.prestation}
                      </Badge>
                      {(user?.role === "admin" || user?.role === "associe") && (() => {
                        const agency = agencies.find(a => a.id === appointment.agency_id);
                        const color = getAgencyColorByAgency(agency);
                        return (
                          <p className="text-xs mt-1 flex items-center justify-end gap-1.5" style={{ color: color.text }} data-testid={`dashboard-apt-agency-${appointment.id}`}>
                            <AgencyDot color={color} className="w-2 h-2" />
                            {agency?.city || "—"}
                          </p>
                        );
                      })()}
                    </div>
                    
                    {/* Prix caché pour les mécanos */}
                    {user?.role !== "mecano" && (
                      <div className="text-right">
                        <span 
                          className="text-lg font-bold" 
                          style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#10B981' }}
                        >
                          {appointment.price} €
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
